import { FunctionComponent, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Box, Divider, Flex, Heading } from '@chakra-ui/react';

import Text from 'components/_core/Text';
import BeneficiariesFollowup from 'features/Onboarding/components/BeneficiariesFollowup';

import { CompanyLegalBeneficiaryOnboardingStatus } from '../../../../../../__generated__/GQL';
import useCompanyContext from '../../../../../../common/hooks/useCompanyContext';
import { useCompanyProfile } from '../../../../../../common/hooks/useCompanyProfile';
import { User } from '../../../../legacy/types';
import { throwIfErrorPresent } from '../../../../utils';
import DownloadAppSection from '../DownloadAppSection';
import CompanyCard from './components/CompanyCard';
import { CompanyCardStatuses } from './components/CompanyCard/CompanyCard';
import SkeletonCard from './components/SkeletonCard';
import { MultiKycUnderReviewDataDocument } from './graphql/queries/multiKycUnderReviewData.gql';
import locales from './locales';

const MultiKycContent: FunctionComponent<{ user: User }> = ({ user }) => {
  const { companyProfileId } = useCompanyContext();

  if (!companyProfileId) {
    throw new Error('companyProfileId must be defined');
  }

  const [isAddingNewBeneficiary, setIsAddingNewBeneficiary] = useState(false);

  // We want to fetch this query from the network to be sure to have the latest
  // beneficairies invitation statuses after all the submission logic.
  const { data, error, loading } = useQuery(MultiKycUnderReviewDataDocument, {
    fetchPolicy: 'network-only',
    variables: {
      companyProfileId,
    },
  });

  throwIfErrorPresent(error);

  const { company } = useCompanyProfile(companyProfileId);

  if (!data || loading) {
    return (
      <Flex direction="column">
        <SkeletonCard />
        <SkeletonCard />
        <SkeletonCard />
      </Flex>
    );
  }

  const beneficiaries = data.viewer.application.legalBeneficiaries.filter(
    (beneficiary) => !beneficiary.isCurrentUser,
  );

  const isActionRequired = beneficiaries.some(
    ({ invitation, onboardingStatus }) =>
      (invitation && !invitation.inviteeEmail) ||
      [
        CompanyLegalBeneficiaryOnboardingStatus.Refused,
        CompanyLegalBeneficiaryOnboardingStatus.Invalid,
      ].includes(onboardingStatus),
  );

  const isComplete = beneficiaries.every(({ onboardingStatus }) =>
    [
      CompanyLegalBeneficiaryOnboardingStatus.InReview,
      CompanyLegalBeneficiaryOnboardingStatus.Validated,
    ].includes(onboardingStatus),
  );

  const getCompanyCardStatus = () => {
    if (isActionRequired) {
      return CompanyCardStatuses.Error;
    }

    if (isComplete) {
      return CompanyCardStatuses.Verifying;
    }

    return CompanyCardStatuses.Pending;
  };

  return (
    <Flex direction="column">
      {isAddingNewBeneficiary ? null : (
        <>
          <Heading marginBottom="space-24" size="lg">
            {locales.heading}
          </Heading>
          <Box marginBottom="space-40">
            {company ? (
              <CompanyCard
                name={company.legalName}
                status={getCompanyCardStatus()}
              />
            ) : (
              <SkeletonCard />
            )}
          </Box>
          <Divider />
          <Text
            fontWeight="weight-500"
            marginY="space-32"
            size="lg"
            variant="primary"
          >
            {locales.beneficiariesFollowup}
          </Text>
        </>
      )}
      <BeneficiariesFollowup
        accountHolderEmail={user.email ?? ''}
        beneficiaries={beneficiaries}
        isAddingNewBeneficiary={isAddingNewBeneficiary}
        onAddBeneficiaryClicked={() => setIsAddingNewBeneficiary(true)}
        onAddBeneficiaryDone={() => setIsAddingNewBeneficiary(false)}
      />
      {isComplete ? (
        <>
          <Divider marginY="space-40" />
          <DownloadAppSection />
        </>
      ) : null}
    </Flex>
  );
};

export default MultiKycContent;
